import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useNavigationPermissions } from "@/hooks/useNavigationPermissions";

type AuthWrapperProps = {
  children: React.ReactNode;
};

export const AuthWrapper = ({ children }: AuthWrapperProps) => {
  const { user, loading, userRole } = useAuth();
  const { canAccessAdminRoutes, canAccessParentRoutes } = useNavigationPermissions();
  const location = useLocation();

  console.log("AuthWrapper Debug:", {
    user: !!user,
    loading,
    userRole,
    metadata: user?.user_metadata,
    path: location.pathname,
    env: import.meta.env.MODE,
    canAccessAdminRoutes,
    canAccessParentRoutes
  });

  // Public paths that don't require authentication
  const publicPaths = ['/', '/auth', '/pricing', '/features', '/blog', '/callback', '/reset-password', '/set-password', '/unauthorized', '/email-verification'];
  
  // Check if current path is public
  const isPublicPath = publicPaths.some(path => 
    location.pathname === path || 
    location.pathname.startsWith('/blog/')
  );

  // In development mode, skip auth checks
  if (import.meta.env.MODE === 'development') {
    console.log("Development mode: skipping auth check");
    return <>{children}</>;
  }

  // Show loading spinner while auth state is loading
  if (loading) {
    console.log("AuthWrapper: Waiting for profile to load");
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Allow access to public paths without authentication
  if (isPublicPath) {
    return <>{children}</>;
  }

  // If not authenticated at all, redirect to auth
  if (!user) {
    console.log("No user found, redirecting to app domain auth");
    
    // Check if we're on marketing domain and need to redirect to app domain
    if (typeof window !== 'undefined' && !window.location.hostname.includes('app.')) {
      // On marketing domain - redirect to app domain for auth
      const appUrl = import.meta.env.DEV 
        ? (import.meta.env.VITE_SITE_URL || 'http://localhost:5173')
        : (import.meta.env.VITE_APP_URL || 'https://app.hivetivity.com');
      window.location.href = appUrl + '/auth';
      return null;
    } else {
      // Already on app domain, use normal navigation
      return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
    }
  }

  // Get user role from metadata
  const hasFamilyId = user.user_metadata?.family_id;
  
  // Parent Portal routes
  const isParentPortalRoute = location.pathname.startsWith('/parent-portal');
  
  // Admin routes (dashboard and admin)
  const isAdminRoute = location.pathname.startsWith('/admin') || 
                       location.pathname === '/dashboard' || 
                       location.pathname.startsWith('/families') || 
                       location.pathname.startsWith('/classes');
  
  // For parent role users who are not admins
  if (userRole === 'parent' && !canAccessAdminRoutes) {
    // If they're trying to access admin routes, redirect to parent portal
    if (isAdminRoute) {
      console.log("Parent user trying to access admin routes, redirecting to parent portal");
      return <Navigate to="/parent-portal" replace />;
    }
    
    // If they're not in the parent portal and should be, redirect them
    if (!isParentPortalRoute && !isPublicPath) {
      return <Navigate to="/parent-portal" replace />;
    }
  }
  
  // Admin users can access both admin and parent portal routes
  if (canAccessAdminRoutes) {
    console.log("Admin user authenticated, allowing access to all routes", { userRole });
    return <>{children}</>;
  }

  console.log("User authenticated, allowing access", { userRole });
  return <>{children}</>;
};
